import { Phone, ArrowUpRight } from "lucide-react";
import { COMPANY } from "../lib/company";

const WORKER_IMG =
  "https://customer-assets.emergentagent.com/job_haul-connect-8/artifacts/st1wupss_483329815_18043756721372381_7629237680280453047_n.jpg";

const GALLERY = [
  {
    src: "https://customer-assets.emergentagent.com/job_haul-connect-8/artifacts/wpfdrsda_491451373_18051336653372381_4999002295066296314_n.webp",
    alt: "Humle Åkeri kranbil lyfter container",
    label: "Transport",
  },
  {
    src: "https://images.unsplash.com/photo-1597974380476-fbf652dfe188?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
    alt: "Grävmaskin i arbete",
    label: "Schakt",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_haul-connect-8/artifacts/hwjxtla3_Screenshot_20260604_175347_Instagram.jpg",
    alt: "Kranväxlare lyfter träd",
    label: "Kranväxlare",
  },
  {
    src: "https://images.unsplash.com/photo-1664312616511-81fe2e745cb3?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
    alt: "Anläggningsarbete",
    label: "Anläggning",
  },
];

export default function OmOss() {
  return (
    <div data-testid="page-om-oss">
      {/* Page Header */}
      <section
        className="bg-black text-white py-20 lg:py-28 relative overflow-hidden"
        data-testid="om-oss-header"
      >
        <div className="hero-grain" />
        <div className="relative mx-auto max-w-[1400px] px-6 lg:px-12">
          <p className="overline text-[#FFD700] mb-5">
            Familjeägt sedan 2020
          </p>
          <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
            Om <span className="text-[#FFD700]">Humlegruppen.</span>
          </h1>
          <p className="mt-6 text-lg text-gray-300 max-w-2xl leading-relaxed">
            En grupp byggd på fyra decenniers erfarenhet av tunga transporter
            och anläggningsarbeten i Storstockholm.
          </p>
        </div>
      </section>

      {/* Story */}
      <section
        className="bg-white py-20 lg:py-28"
        data-testid="om-oss-story"
      >
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-6">
            <p className="overline text-gray-500 mb-4">Vår historia</p>
            <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-black leading-[1] tracking-tight">
              Ungt bolag.
              <br />
              <span className="text-[#166534]">Tung erfarenhet.</span>
            </h2>
            <div className="mt-8 space-y-5 text-gray-700 text-base lg:text-lg leading-relaxed">
              <p>
                Humle Åkeri AB grundades 2020 i Tungelsta — av människor med
                decenniers samlad erfarenhet av tunga transporter och
                anläggningsbranschen. Vi startade med ett tydligt mål: leverera
                i tid, hålla det vi lovar och vara den åkare våra kunder
                föredrar att ringa först.
              </p>
              <p>
                Snart följde Humle Entreprenad AB för att kunna ta ansvar för
                hela kedjan — schakt, mark, rivning och transport. Tillsammans
                utgör de två bolagen Humlegruppen.
              </p>
              <p>
                Vi är lokalt förankrade i Haninge, drivs som ett familjeföretag
                och tror på rak kommunikation, välskötta maskiner och människor
                som vet vad de gör.
              </p>
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="relative border-2 border-black brut-shadow">
              <img
                src={WORKER_IMG}
                alt="Yrkesarbetare på byggarbetsplats"
                className="w-full aspect-[4/5] object-cover"
              />
              <div className="absolute top-6 left-6 bg-[#FFD700] border-2 border-black px-4 py-2 font-condensed uppercase tracking-wider font-bold">
                Sedan 2020
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section
        className="bg-[#F3F4F6] py-20 lg:py-28"
        data-testid="om-oss-values"
      >
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <p className="overline text-gray-500 mb-4">Våra värderingar</p>
          <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-black leading-[1] tracking-tight max-w-3xl">
            Lika gediget arbete idag som dag ett.
          </h2>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              {
                k: "Hantverk",
                d: "Vi är stolta yrkesmänniskor. Det märks i precision, planering och utförande.",
              },
              {
                k: "Ansvar",
                d: "Det vi lovar håller vi. Det vi inte hinner säger vi i förväg.",
              },
              {
                k: "Närhet",
                d: "Vi är lokala på riktigt. Du når alltid en person som känner ditt projekt.",
              },
            ].map((v, i) => (
              <div
                key={i}
                className="bg-white border-2 border-black p-8"
                data-testid={`value-card-${i}`}
              >
                <p className="font-condensed uppercase tracking-wider text-sm font-bold text-[#166534] mb-3">
                  0{i + 1}
                </p>
                <h3 className="font-display font-black text-3xl text-black mb-3">
                  {v.k}
                </h3>
                <p className="text-gray-700 leading-relaxed">{v.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Equipment Gallery */}
      <section
        className="bg-white py-20 lg:py-28"
        data-testid="om-oss-gallery"
      >
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
            <div>
              <p className="overline text-gray-500 mb-4">Vår flotta</p>
              <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-black leading-[1] tracking-tight max-w-2xl">
                Maskiner och fordon i arbete.
              </h2>
            </div>
            <p className="text-gray-600 text-base max-w-md">
              Vår flotta uppdateras kontinuerligt med moderna, miljöklassade
              fordon och tunga anläggningsmaskiner.
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {GALLERY.map((g, i) => (
              <div
                key={i}
                className="relative aspect-square border-2 border-black overflow-hidden group"
                data-testid={`gallery-item-${i}`}
              >
                <img
                  src={g.src}
                  alt={g.alt}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <p className="absolute bottom-4 left-4 font-condensed uppercase tracking-wider text-sm font-bold text-[#FFD700]">
                  {g.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        className="bg-black text-white py-16 lg:py-24"
        data-testid="om-oss-cta"
      >
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl leading-tight max-w-2xl">
            Vill du veta mer om vad{" "}
            <span className="text-[#FFD700]">vi kan göra för dig?</span>
          </h2>
          <div className="flex flex-wrap gap-4">
            <a
              href={COMPANY.phoneHref}
              className="inline-flex items-center gap-3 bg-[#FFD700] text-black font-bold px-7 py-4 border-2 border-[#FFD700] hover:bg-yellow-400 transition-colors"
              data-testid="om-oss-cta-phone"
            >
              <Phone size={20} strokeWidth={2.5} />
              <span className="font-condensed uppercase tracking-wider text-base">
                {COMPANY.phone}
              </span>
            </a>
            <a
              href={COMPANY.emailHref}
              className="inline-flex items-center gap-3 bg-transparent text-white font-bold px-7 py-4 border-2 border-white hover:bg-white hover:text-black transition-colors"
              data-testid="om-oss-cta-email"
            >
              <span className="font-condensed uppercase tracking-wider text-base">
                Skicka e-post
              </span>
              <ArrowUpRight size={18} />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
